// Shared jsPDF skeleton for report PDFs. Each report calls buildReportPdf
// with the report's title, subtitle, plain text body (from buildXxxReport),
// and filename. Matches the visual language of the calculator PDFs and DDR.
import jsPDF from "jspdf";

const COLORS = {
  amber: [255, 179, 0],
  dark: [10, 11, 14],
  text: [33, 37, 44],
  muted: [104, 113, 120],
  border: [200, 200, 200],
};

function header(doc, title, subtitle, brandLine) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, w, 22, "F");
  doc.setFillColor(...COLORS.amber);
  doc.rect(0, 22, w, 1.2, "F");
  doc.setTextColor(...COLORS.amber);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("RigSentinel", 12, 11);
  doc.setTextColor(220, 220, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(brandLine, 12, 17);
  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(title, 12, 34);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);
    doc.text(subtitle, 12, 39);
  }
}

function footer(doc, pageNum, total) {
  const h = doc.internal.pageSize.getHeight();
  const w = doc.internal.pageSize.getWidth();
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.2);
  doc.line(12, h - 14, w - 12, h - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.muted);
  doc.text(
    "RigSentinel · Draft documentation support only. Review, correct, and approve under company procedures before submission.",
    12,
    h - 9,
  );
  doc.text(`Page ${pageNum} / ${total}`, w - 12, h - 9, { align: "right" });
}

export function buildReportPdf({ title, subtitle, brandLine, body, filename }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageH = doc.internal.pageSize.getHeight();
  const bottom = pageH - 20;
  header(doc, title, subtitle || "", brandLine);
  let y = 48;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);

  const lines = (body || "").split("\n");
  for (const line of lines) {
    if (line.startsWith("=== ") && line.endsWith(" ===")) {
      if (y > bottom - 12) { doc.addPage(); header(doc, title, subtitle || "", brandLine); y = 48; }
      const label = line.replace(/^===\s*/, "").replace(/\s*===$/, "");
      doc.setDrawColor(...COLORS.amber);
      doc.setLineWidth(0.5);
      doc.line(12, y, 18, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.amber);
      doc.text(label.toUpperCase(), 22, y + 1);
      y += 6;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.text);
      continue;
    }
    const wrapped = doc.splitTextToSize(line || " ", 186);
    for (const wln of wrapped) {
      if (y > bottom) { doc.addPage(); header(doc, title, subtitle || "", brandLine); y = 48; }
      doc.text(wln, 12, y);
      y += 4.6;
    }
  }

  if (y > pageH - 60) { doc.addPage(); header(doc, title, subtitle || "", brandLine); y = 48; }
  doc.setDrawColor(...COLORS.amber);
  doc.setLineWidth(0.4);
  doc.rect(12, y + 2, 186, 38);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.amber);
  doc.text("Disclaimer", 16, y + 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.text);
  const dis = doc.splitTextToSize(
    "RigSentinel report tools provide draft documentation support only. All reports must be reviewed, corrected, and approved by competent rig/site personnel under company procedures, client requirements, and applicable regulations before submission.",
    178,
  );
  let dy = y + 15;
  for (const d of dis) { doc.text(d, 16, dy); dy += 4.4; }

  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); footer(doc, i, total); }
  doc.save(filename);
}
